import {
  ensureDatabricksAiGateway,
  listDatabricksModelProviderServices,
  listDatabricksModelServices,
  type DatabricksModelProviderService,
  type DatabricksModelService,
  type DatabricksModelServiceDiscoveryOptions,
} from "./ai-gateway.js";
import { databricksIdentity, type DatabricksPrincipal } from "./identity.js";
import { DatabricksRawProtocolClient } from "./raw-protocol-client.js";
import { listDatabricksSqlWarehouses, type DatabricksSqlWarehouse } from "./sql-warehouses.js";

/** Runtime-only control-plane configuration for applications and server frameworks. */
export interface DatabricksControlPlaneOptions {
  host: string;
  principal: DatabricksPrincipal;
  fetchImpl?: typeof fetch;
  extraHeaders?:
    | Record<string, string>
    | (() => Record<string, string> | Promise<Record<string, string>>);
}

/**
 * A compact Databricks control-plane client that deliberately excludes Harness build, deployment,
 * and agent-authoring code. Use this entrypoint from application bundles such as Next.js.
 */
export function databricksControlPlane(options: DatabricksControlPlaneOptions) {
  const client = new DatabricksRawProtocolClient({
    host: options.host,
    token: databricksIdentity(options.principal),
    ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
    ...(options.extraHeaders ? { extraHeaders: options.extraHeaders } : {}),
  });
  return {
    aiGateway: {
      ensure: (): Promise<void> => ensureDatabricksAiGateway(client),
      listModelServices: (
        discoveryOptions?: DatabricksModelServiceDiscoveryOptions,
      ): Promise<DatabricksModelService[]> => listDatabricksModelServices(client, discoveryOptions),
      listModelProviderServices: (): Promise<DatabricksModelProviderService[]> =>
        listDatabricksModelProviderServices(client),
    },
    sqlWarehouses: {
      list: (): Promise<DatabricksSqlWarehouse[]> => listDatabricksSqlWarehouses(client),
    },
  };
}

export {
  databricksModelProviderSupportsAnthropic,
  type DatabricksModelProviderService,
  type DatabricksModelService,
  type DatabricksModelServiceDiscoveryOptions,
} from "./ai-gateway.js";
export {
  createDatabricksAuthenticatedFetch,
  databricksAnthropicGatewayBaseUrl,
  type DatabricksAuthenticatedFetchOptions,
} from "./authenticated-fetch.js";
export {
  databricksIdentity,
  databricksPrincipalFromEnv,
  type DatabricksPrincipal,
  type DatabricksTokenProvider,
} from "./identity.js";
export { lakebaseCredentialProvider } from "./lakebase.js";
export {
  databricksSdk,
  type DatabricksSdkClients,
  type DatabricksSdkOptions,
} from "./native-sdk.js";
export {
  parseStatementRows,
  runStatement,
  type SqlParameter,
  type StatementPoll,
} from "./sql-exec.js";
export {
  chooseDatabricksSqlWarehouse,
  type DatabricksSqlWarehouse,
} from "./sql-warehouses.js";
export { databricksWorkspaceOrigin } from "./workspace-origin.js";
